import {
  Invoices_DownloadDetailTypes,
  Invoices_InvoiceLineCalculation
} from "data/endpoints";
import PageCard from "features/shared/components/card/PageCard";
import PagedDataResultDTO from "features/shared/dtos/PagedDataResultDTO";
import { getRouteParams } from "features/shared/helpers/routeParams";
import React, { useState } from "react";
import InvoiceLineCalculationDTO from "../dtos/InvoiceLineCalculationDTO";
import InvoiceLineCalculation from "../components/InvoiceLineCalculation";
import InvoiceLineCalculationHeader from "../components/InvoiceLineCalculationHeader";
import InvoiceTotalAmount from "../components/InvoiceTotalAmount";
import InvoiceDTO from "../dtos/InvoiceDTO";
import { InvoiceRecapitulationType } from "../enums/invoiceRecapitulationType";
import InvoiceGridPagination from "../components/InvoiceGridPagination";
import DataFilterModal from "features/shared/components/dataFilterModal/DataFilterModal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import DataQuery from "data/DataQuery";
import { useQuery } from "data/hooks/useQuery";
import ConditionalExecute from "data/models/ConditionalExecute";
import NoContentWrapper from "features/shared/components/noContentWrapper/NoContentWrapper";
import { isEmpty } from "features/shared/helpers/objects";
import { loadDataFilterConfig } from "features/shared/helpers/modules";
import CardHeaderButton from "features/shared/components/card/CardHeaderButton";
import { useStateDataGrid } from "features/shared/components/dataGrid/hooks/useStateDataGrid";
import { useTranslation } from "react-i18next";
import InvoiceLineDownloadDetailDTO from "../dtos/InvoiceLineDownloadDetailDTO";

const linesCalculationDataFilterJson = loadDataFilterConfig("invoiceDetailLinesCalculationDataFilter");
const pageSize = 5;

interface Props {
  invoice: InvoiceDTO;
}

const InvoiceLinesCalculationSection = ({ invoice }: Props) => {
  //#region translations
  const tLabels = useTranslation("invoice\\invoice").t;
  //#endregion translations

  //#region  STATE
  const [wasOpenedFilter, setWasOpenedFilter] = useState(false);
  //#endregion  STATE

  //#region data grid hook
  const {
    dataGrid,
    descriptors,
    setDescriptors,
    showFilter,
    setShowFilter,
    paginationPageChangeHandler,
    filterApplyHandler,
    filterResetHandler
  } = useStateDataGrid(pageSize, linesCalculationDataFilterJson);
  //#endregion data grid hook

  //#region FETCH DATA
  // invoice lines
  const conditionalExecute = () => {
    if (dataGrid.paginationUrlSearchParams) return true;
    if (dataGrid.filtrUrlSearchParams) return true;

    return false;
  };

  const linesQuery: DataQuery = {
    url: Invoices_InvoiceLineCalculation,
    routeParams: getRouteParams("invoiceId", invoice.invoiceId!),
    params: new URLSearchParams(dataGrid.computedUrlSearchParams),
  };
  const execute: ConditionalExecute = {
    apply: true,
    condition: conditionalExecute,
  };

  const { data: invoiceLines, loadStatus: invoiceLinesStatus } = useQuery<
    PagedDataResultDTO<InvoiceLineCalculationDTO>
  >(
    linesQuery,
    { result: [], count: 0 },
    [invoice, dataGrid.computedUrlSearchParams],
    { useTracker: true },
    execute
  );

  // downloads for invoice
  const { data: invoiceLineDownloadDetails, loadStatus: invoiceLineDownloadDetailsStatus } = useQuery<InvoiceLineDownloadDetailDTO[]>(
    {
      url: Invoices_DownloadDetailTypes,
      routeParams: getRouteParams("invoiceId", invoice.invoiceId!),
    },
    [],
    [invoice],
    undefined,
    {
      apply: true,
      condition: () => !isEmpty(invoice),
    }
  );
  //#endregion FETCH DATA

  // //#region HANDLERS
   const openFilterHandler = () => {
    setShowFilter(true);
    if (!wasOpenedFilter) setWasOpenedFilter(true);
  };
  //#endregion HANDLERS

  //#region SUB-RENDERS
  const renderInvoiceLines = () => {
    return invoiceLines.result.map((item) => (
      <div className="py-1 py-sm-2" key={item.invoiceLineId}>
        <InvoiceLineCalculation
          invoiceLine={item}
          downloadDetails={invoiceLineDownloadDetails.filter((x) => x.invoiceLineId === item.invoiceLineId)}
        />
      </div>
    ));
  };

  const renderActionBtns = () => {
    let result: JSX.Element[] = [];

    if (invoiceLines.count > 1 || wasOpenedFilter) {
      let elem = (
        <CardHeaderButton
          type="button"
          onClick={openFilterHandler}
          key={invoice.invoiceId}
        >
          <FontAwesomeIcon icon="filter" />
        </CardHeaderButton>
      );
      result.push(elem);
    }

    return result;
  };
  //#endregion SUB-RENDERS

  //#region RENDER
  return (
    <PageCard title={tLabels("invoiceLinesCalculationSection.cardTitle")} headerActionBtns={renderActionBtns}>
      <NoContentWrapper
        data={invoiceLines}
        loadStatus={[invoiceLinesStatus, invoiceLineDownloadDetailsStatus]}
      >
        <InvoiceLineCalculationHeader />
        {renderInvoiceLines()}
        <div className="d-flex flex-column flex-sm-row align-items-center mt-2 mb-1 pt-3 pr-sm-5 border-top">
          <InvoiceGridPagination
            urlParams={new URLSearchParams(dataGrid.paginationUrlSearchParams)}
            count={invoiceLines.count}
            pageSize={pageSize}
            onPageChange={paginationPageChangeHandler}
          />
          <InvoiceTotalAmount
            invoiceRecapitulation={invoice.invoiceRecapitulation!}
            recapitulationType={InvoiceRecapitulationType.InvoiceLinesTotal}
            currency={invoice.currency!}
            includeTax={false}
          />
        </div>
      </NoContentWrapper>
      {(invoiceLines.count > 1 || wasOpenedFilter) && (
        <DataFilterModal
          descriptors={descriptors}
          setDescriptors={setDescriptors}
          applyButtonOnClick={filterApplyHandler}
          resetButtonOnClick={filterResetHandler}
          show={showFilter}
          setShow={setShowFilter}
        />
      )}
    </PageCard>
  );
  //#endregion RENDER
};

export default InvoiceLinesCalculationSection;
